/**
 * Line Item Library - Pre-defined income statement line items
 * Based on retail-financials-prd.md Appendix A
 */

import type { LineItemCategoryGroup } from '../types/financials.types'

export const LINE_ITEM_LIBRARY: LineItemCategoryGroup[] = [
	{
		id: 'revenue_detail',
		label: 'Revenue Detail',
		items: [
			{
				id: 'merchandise_sales',
				label: 'Merchandise Sales',
				category: 'revenue',
				description: 'Sales from physical merchandise and products',
			},
			{
				id: 'services_revenue',
				label: 'Services Revenue',
				category: 'revenue',
				description: 'Revenue from services provided',
			},
			{
				id: 'other_revenue',
				label: 'Other Revenue',
				category: 'revenue',
				description: 'Miscellaneous revenue not categorized elsewhere',
			},
			{
				id: 'returns_allowances',
				label: 'Returns & Allowances',
				category: 'revenue',
				description: 'Customer returns and sales allowances (negative revenue)',
			},
		],
	},
	{
		id: 'cogs_detail',
		label: 'COGS Detail',
		items: [
			{
				id: 'inventory_cost',
				label: 'Inventory Cost',
				category: 'cogs',
				description: 'Direct cost of inventory sold',
			},
			{
				id: 'shrinkage',
				label: 'Shrinkage / Losses',
				category: 'cogs',
				description: 'Inventory losses due to theft, damage, or errors',
			},
			{
				id: 'freight_shipping',
				label: 'Freight & Shipping In',
				category: 'cogs',
				description: 'Shipping costs for incoming inventory',
			},
			{
				id: 'import_duties',
				label: 'Import Duties',
				category: 'cogs',
				description: 'Customs and import taxes on goods',
			},
		],
	},
	{
		id: 'opex_occupancy',
		label: 'Operating Expenses - Occupancy',
		items: [
			{
				id: 'rent',
				label: 'Rent / Lease Expense',
				category: 'operating_expense',
				description: 'Monthly or annual rent payments',
				isCommon: true,
			},
			{
				id: 'cam',
				label: 'Common Area Maintenance (CAM)',
				category: 'operating_expense',
				description: 'Fees for shared space maintenance in retail centers',
			},
			{
				id: 'property_taxes',
				label: 'Property Taxes',
				category: 'operating_expense',
				description: 'Real estate taxes on the property',
			},
			{
				id: 'insurance_property',
				label: 'Insurance - Property',
				category: 'operating_expense',
				description: 'Property and liability insurance',
			},
			{
				id: 'utilities_electric',
				label: 'Utilities - Electric',
				category: 'operating_expense',
				description: 'Electricity costs',
				isCommon: true,
			},
			{
				id: 'utilities_gas',
				label: 'Utilities - Gas',
				category: 'operating_expense',
				description: 'Natural gas costs',
			},
			{
				id: 'utilities_water',
				label: 'Utilities - Water',
				category: 'operating_expense',
				description: 'Water and sewer costs',
			},
			{
				id: 'repairs_maintenance',
				label: 'Repairs & Maintenance - Building',
				category: 'operating_expense',
				description: 'Building and facility maintenance',
			},
			{
				id: 'janitorial',
				label: 'Janitorial / Cleaning',
				category: 'operating_expense',
				description: 'Cleaning services and supplies',
			},
		],
	},
	{
		id: 'opex_labor',
		label: 'Operating Expenses - Labor',
		items: [
			{
				id: 'labor_costs',
				label: 'Labor Costs (Total)',
				category: 'operating_expense',
				description: 'Total labor and payroll costs',
				isCommon: true,
			},
			{
				id: 'wages_ft',
				label: 'Wages - Full Time',
				category: 'operating_expense',
				description: 'Full-time employee wages',
			},
			{
				id: 'wages_pt',
				label: 'Wages - Part Time',
				category: 'operating_expense',
				description: 'Part-time employee wages',
			},
			{
				id: 'overtime',
				label: 'Overtime Pay',
				category: 'operating_expense',
				description: 'Overtime wages',
			},
			{
				id: 'payroll_taxes',
				label: 'Payroll Taxes',
				category: 'operating_expense',
				description: 'Employer payroll tax contributions',
			},
			{
				id: 'benefits_health',
				label: 'Benefits - Health Insurance',
				category: 'operating_expense',
				description: 'Employee health insurance costs',
			},
			{
				id: 'benefits_retirement',
				label: 'Benefits - Retirement',
				category: 'operating_expense',
				description: '401k or pension contributions',
			},
			{
				id: 'benefits_other',
				label: 'Benefits - Other',
				category: 'operating_expense',
				description: 'Other employee benefits',
			},
			{
				id: 'workers_comp',
				label: "Workers' Compensation Insurance",
				category: 'operating_expense',
				description: "Workers' compensation insurance premiums",
			},
			{
				id: 'training',
				label: 'Training & Development',
				category: 'operating_expense',
				description: 'Employee training costs',
			},
		],
	},
	{
		id: 'opex_marketing',
		label: 'Operating Expenses - Sales & Marketing',
		items: [
			{
				id: 'marketing_advertising',
				label: 'Marketing & Advertising',
				category: 'operating_expense',
				description: 'General marketing and advertising expenses',
			},
			{
				id: 'digital_marketing',
				label: 'Digital Marketing',
				category: 'operating_expense',
				description: 'Online advertising and digital campaigns',
			},
			{
				id: 'print_advertising',
				label: 'Print Advertising',
				category: 'operating_expense',
				description: 'Print ads, flyers, and mailers',
			},
			{
				id: 'promotions_events',
				label: 'Promotions & Events',
				category: 'operating_expense',
				description: 'Promotional events and in-store promotions',
			},
			{
				id: 'loyalty_program',
				label: 'Loyalty Program Costs',
				category: 'operating_expense',
				description: 'Customer loyalty program expenses',
			},
			{
				id: 'public_relations',
				label: 'Public Relations',
				category: 'operating_expense',
				description: 'PR services and media relations',
			},
		],
	},
	{
		id: 'opex_store',
		label: 'Operating Expenses - Store Operations',
		items: [
			{
				id: 'store_supplies',
				label: 'Store Supplies',
				category: 'operating_expense',
				description: 'Operating supplies and consumables',
			},
			{
				id: 'pos_fees',
				label: 'Point of Sale (POS) Fees',
				category: 'operating_expense',
				description: 'POS system fees and maintenance',
			},
			{
				id: 'credit_card_fees',
				label: 'Credit Card Processing Fees',
				category: 'operating_expense',
				description: 'Payment processing fees',
			},
			{
				id: 'bank_fees',
				label: 'Bank Fees',
				category: 'operating_expense',
				description: 'Bank account and transaction fees',
			},
			{
				id: 'security',
				label: 'Security',
				category: 'operating_expense',
				description: 'Security services and systems',
			},
			{
				id: 'cash_over_short',
				label: 'Cash Over/Short',
				category: 'operating_expense',
				description: 'Cash register discrepancies',
			},
			{
				id: 'uniforms',
				label: 'Uniforms',
				category: 'operating_expense',
				description: 'Employee uniform costs',
			},
		],
	},
	{
		id: 'opex_admin',
		label: 'Operating Expenses - General & Administrative',
		items: [
			{
				id: 'office_supplies',
				label: 'Office Supplies',
				category: 'operating_expense',
				description: 'Administrative office supplies',
			},
			{
				id: 'technology_software',
				label: 'Technology & Software',
				category: 'operating_expense',
				description: 'Software licenses and IT costs',
			},
			{
				id: 'telephone_internet',
				label: 'Telephone / Internet',
				category: 'operating_expense',
				description: 'Phone and internet services',
			},
			{
				id: 'legal_fees',
				label: 'Professional Fees - Legal',
				category: 'operating_expense',
				description: 'Legal services and counsel',
			},
			{
				id: 'accounting_fees',
				label: 'Professional Fees - Accounting',
				category: 'operating_expense',
				description: 'Accounting and bookkeeping services',
			},
			{
				id: 'licenses_permits',
				label: 'Licenses & Permits',
				category: 'operating_expense',
				description: 'Business licenses and permits',
			},
			{
				id: 'travel_entertainment',
				label: 'Travel & Entertainment',
				category: 'operating_expense',
				description: 'Business travel and entertainment expenses',
			},
			{
				id: 'dues_subscriptions',
				label: 'Dues & Subscriptions',
				category: 'operating_expense',
				description: 'Professional memberships and subscriptions',
			},
			{
				id: 'depreciation_equipment',
				label: 'Depreciation - Equipment',
				category: 'operating_expense',
				description: 'Equipment depreciation expense',
			},
			{
				id: 'depreciation_fixtures',
				label: 'Depreciation - Fixtures',
				category: 'operating_expense',
				description: 'Store fixtures and displays depreciation',
			},
			{
				id: 'amortization',
				label: 'Amortization',
				category: 'operating_expense',
				description: 'Amortization of intangible assets',
			},
		],
	},
	{
		id: 'opex_other',
		label: 'Operating Expenses - Other',
		items: [
			{
				id: 'other_opex',
				label: 'Other Operating Expenses',
				category: 'operating_expense',
				description: 'Miscellaneous operating expenses',
			},
		],
	},
	// A property owner's statement, for a family office or any landlord (MYD-652).
	// Everything above is written from the tenant's side of the lease: "Rent" is
	// an expense there. These are the same dollars seen from the other side, so
	// they need their own ids rather than a relabelled "rent".
	{
		id: 'owner_income',
		label: 'Property Owner - Income',
		items: [
			{
				id: 'base_rent',
				label: 'Base Rent',
				category: 'revenue',
				description: 'Minimum or base rent billed to tenants under their leases',
			},
			{
				id: 'percentage_rent',
				label: 'Percentage Rent',
				category: 'revenue',
				description: 'Rent above base rent, owed when tenant sales pass a breakpoint',
			},
			{
				id: 'cam_recoveries',
				label: 'CAM Recoveries',
				category: 'revenue',
				description: 'Common area maintenance costs billed back to tenants',
			},
			{
				id: 'tax_recoveries',
				label: 'Real Estate Tax Recoveries',
				category: 'revenue',
				description: 'Property taxes billed back to tenants under net leases',
			},
			{
				id: 'insurance_recoveries',
				label: 'Insurance Recoveries',
				category: 'revenue',
				description: 'Property insurance billed back to tenants',
			},
			{
				id: 'other_property_income',
				label: 'Other Property Income',
				category: 'revenue',
				description: 'Parking, signage, late fees and other income from the property',
			},
		],
	},
	{
		id: 'owner_expenses',
		label: 'Property Owner - Operating Expenses',
		items: [
			{
				id: 'property_management_fee',
				label: 'Property Management Fee',
				category: 'operating_expense',
				description: "The property manager's fee, usually a percentage of collected revenue",
			},
			{
				id: 'common_area_costs',
				label: 'Common Area Operating Costs',
				category: 'operating_expense',
				description: 'Cleaning, lighting, security and upkeep of shared areas',
			},
			{
				id: 'landscaping',
				label: 'Landscaping',
				category: 'operating_expense',
				description: 'Grounds and landscape maintenance',
			},
			{
				id: 'parking_lot_maintenance',
				label: 'Parking Lot Maintenance',
				category: 'operating_expense',
				description: 'Sweeping, striping, sealing and lot repairs',
			},
			{
				id: 'non_recoverable_expenses',
				label: 'Non-Recoverable Expenses',
				category: 'operating_expense',
				description: 'Owner costs that the leases do not allow to be billed back',
			},
			{
				id: 'replacement_reserves',
				label: 'Replacement Reserves',
				category: 'operating_expense',
				description: 'Set-aside for roofs, parking and other capital replacement',
			},
		],
	},
	{
		id: 'owner_below_noi',
		label: 'Property Owner - Below NOI',
		items: [
			{
				id: 'leasing_commissions',
				label: 'Leasing Commissions',
				category: 'other',
				description: 'Broker commissions on new leases and renewals',
			},
			{
				id: 'tenant_improvements',
				label: 'Tenant Improvements',
				category: 'other',
				description: 'Landlord-funded build-out for new or renewing tenants',
			},
			{
				id: 'capital_expenditures',
				label: 'Capital Expenditures',
				category: 'other',
				description: 'Roof, parking, facade and other capital work',
			},
			{
				id: 'debt_service',
				label: 'Debt Service',
				category: 'other',
				description: 'Mortgage principal and interest',
			},
		],
	},
	{
		id: 'non_operating',
		label: 'Non-Operating Items',
		items: [
			{
				id: 'interest_expense',
				label: 'Interest Expense',
				category: 'other',
				description: 'Interest on debt and loans',
			},
			{
				id: 'interest_income',
				label: 'Interest Income',
				category: 'other',
				description: 'Interest earned on deposits',
			},
			{
				id: 'gain_loss_asset',
				label: 'Gain/Loss on Asset Sale',
				category: 'other',
				description: 'Gains or losses from asset disposals',
			},
			{
				id: 'other_non_operating',
				label: 'Other Non-Operating Income/Expense',
				category: 'other',
				description: 'Other non-operating items',
			},
		],
	},
]

// Default line items that are always present
export const DEFAULT_LINE_ITEMS = [
	{
		id: 'sales',
		label: 'Sales',
		type: 'input' as const,
		category: 'revenue' as const,
		isDefault: true,
		order: 1,
	},
	{
		id: 'cogs',
		label: 'COGS',
		type: 'input' as const,
		category: 'cogs' as const,
		isDefault: true,
		order: 2,
	},
	{
		id: 'gross_profit',
		label: 'Gross Profit',
		type: 'calculated' as const,
		category: 'gross_profit' as const,
		formula: 'sales - cogs',
		isDefault: true,
		order: 3,
	},
	{
		id: 'expenses',
		label: 'Expenses',
		type: 'input' as const,
		category: 'operating_expense' as const,
		isDefault: true,
		order: 4,
	},
	{
		id: 'earnings',
		label: 'Earnings',
		type: 'calculated' as const,
		category: 'earnings' as const,
		formula: 'gross_profit - expenses',
		isDefault: true,
		order: 5,
	},
]

/**
 * A statement is laid out for a retail operator or for a property owner.
 *
 * Both layouts use the SAME five default ids, because a landlord's statement
 * has the same shape: Rental Revenue, less Vacancy & Credit Loss, is Effective
 * Gross Income; less Operating Expenses, it is Net Operating Income. Keeping the
 * ids means the portfolio roll-up, charts, presentations and import all work on
 * an owner's statement unchanged. Only the labels differ, and labels are stored
 * on each statement.
 */
export type StatementLayout = 'operator' | 'owner'

const OWNER_DEFAULT_LABELS: Record<string, string> = {
	sales: 'Rental Revenue',
	cogs: 'Vacancy & Credit Loss',
	gross_profit: 'Effective Gross Income',
	expenses: 'Operating Expenses',
	earnings: 'Net Operating Income',
}

export const defaultLineItemsFor = (layout: StatementLayout) =>
	layout === 'owner'
		? DEFAULT_LINE_ITEMS.map(item => ({ ...item, label: OWNER_DEFAULT_LABELS[item.id] ?? item.label }))
		: DEFAULT_LINE_ITEMS

/** The label a default row takes in a layout, for rows added back during an import. */
export const defaultLabelFor = (id: string, layout: StatementLayout): string =>
	defaultLineItemsFor(layout).find(item => item.id === id)?.label ?? id

const OWNER_ITEM_IDS = new Set(
	LINE_ITEM_LIBRARY.filter(group => group.id.startsWith('owner_')).flatMap(group =>
		group.items.map(item => item.id),
	),
)

/**
 * Which layout a set of rows belongs to. An owner statement is recognised by
 * its default labels or by any owner-only row, so a statement keeps its layout
 * through an import without the layout having to be stored anywhere.
 */
export const detectLayout = (lineItems: Array<{ id?: string; label?: string }>): StatementLayout =>
	lineItems.some(
		item =>
			OWNER_ITEM_IDS.has(item.id ?? '') ||
			(item.id !== undefined && OWNER_DEFAULT_LABELS[item.id] === item.label),
	)
		? 'owner'
		: 'operator'

